import { ReactNode } from 'react';

import { Alert, Box, Paper, Stack, Typography } from '@mui/material';

import { ClassSeriesSelector } from './ClassSeriesSelector';
import { CompetenciaLabel } from './CompetenciaLabel';
import { HistoricalProfitabilityTable } from './HistoricalProfitabilityTable';
import { LineChart, sectionSx } from './chartShared';
import {
  creditRightsOverPlHelp,
  monthlyCdiHelp,
  pddOverAtrasoHelp,
  PROFITABILITY_PERIOD_OPTIONS,
} from './constants';
import { PERCENT_FORMATTER, SIGNED_PERCENT_FORMATTER } from './formatters';
import {
  FundPerformancePeriodKey,
  ProfitabilityPeriodKey,
  SelectedFund,
} from './types';
import { useFundPerformance } from './useFundPerformance';
import { useHistoricalProfitability } from './useHistoricalProfitability';

const RentabilidadeHelp = () => (
  <Box>
    <Typography variant="caption" display="block" fontWeight={600} mb={0.5}>
      Rentabilidade Mensal (%)
    </Typography>
    <Typography variant="caption" display="block" lineHeight={1.6} mb={1}>
      Variação percentual da cota de cada FIDC em relação ao mês anterior,
      conforme os <strong>informes mensais</strong> enviados pelos
      administradores à <strong>CVM</strong>.
    </Typography>
    <Typography variant="caption" display="block" lineHeight={1.8}>
      <strong>+valor</strong>: cota valorizou no período.
      <br />
      <strong>−valor</strong>: cota desvalorizou no período.
      <br />
      <strong>Sem dado</strong>: informe não disponível. Pode indicar atraso do
      administrador, período anterior ao início do fundo ou classe sem informe
      mensal na CVM.
    </Typography>
  </Box>
);

interface FidcComparisonChartsProps {
  rentabilityControls?: ReactNode;
  selectedFunds?: SelectedFund[];
  baseMonth?: string;
}

export default function FidcComparisonCharts({
  rentabilityControls,
  selectedFunds,
  baseMonth,
}: FidcComparisonChartsProps) {
  const {
    handleSelectionsChange,
    noDataFunds,
    isProfitabilityLoading,
    isProfitabilityError,
    profitabilityData,
    uniqueFunds,
    accumulatedSeries,
    accumulatedPeriod,
    setAccumulatedPeriod,
    monthlyReturnsSeries,
    monthlyReturnsPeriod,
    setMonthlyReturnsPeriod,
    monthlyCdiSeries,
    monthlyCdiPeriod,
    setMonthlyCdiPeriod,
  } = useHistoricalProfitability(selectedFunds, baseMonth);

  const {
    isFundPerfLoading,
    isFundPerfError,
    pddSeries,
    dcSeries,
    pddPeriod,
    setPddPeriod,
    dcPeriod,
    setDcPeriod,
  } = useFundPerformance(selectedFunds, baseMonth);

  const competenciaSubtitle = <CompetenciaLabel baseMonth={baseMonth} />;

  return (
    <Stack gap={3}>
      {selectedFunds && selectedFunds.length > 0 && (
        <Paper variant="outlined" sx={sectionSx}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}
            mb={2.5}>
            <Stack gap={0.5}>
              <Typography variant="h6" fontWeight={700}>
                Performance do fundo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Relações de cobertura e enquadramento para os FIDCs
                selecionados.
              </Typography>
            </Stack>
          </Stack>
          <Stack gap={2}>
            {isFundPerfError && (
              <Alert severity="error" variant="outlined">
                Erro ao carregar dados de performance dos fundos selecionados.
              </Alert>
            )}
            {isFundPerfLoading && (
              <Alert severity="info" variant="outlined">
                Carregando performance dos fundos selecionados...
              </Alert>
            )}
            <LineChart
              title="PDD / Atraso"
              titleHelp={pddOverAtrasoHelp}
              subtitle={competenciaSubtitle}
              series={pddSeries}
              yAxisFormatter={PERCENT_FORMATTER}
              tooltipFormatter={PERCENT_FORMATTER}
              selectedPeriod={pddPeriod}
              periodOptions={PROFITABILITY_PERIOD_OPTIONS}
              onPeriodChange={(p) =>
                setPddPeriod(p as FundPerformancePeriodKey)
              }
              selectorId="pdd-over-atraso"
              noDataText="Sem dados de PDD / atraso para a competência selecionada"
            />
            <LineChart
              title="DC / PL"
              titleHelp={creditRightsOverPlHelp}
              subtitle={competenciaSubtitle}
              series={dcSeries}
              yAxisFormatter={PERCENT_FORMATTER}
              tooltipFormatter={PERCENT_FORMATTER}
              selectedPeriod={dcPeriod}
              periodOptions={PROFITABILITY_PERIOD_OPTIONS}
              onPeriodChange={(p) => setDcPeriod(p as FundPerformancePeriodKey)}
              selectorId="dc-over-pl"
              noDataText="Sem dados de DC / PL para a competência selecionada"
            />
          </Stack>
        </Paper>
      )}
      <Paper variant="outlined" sx={sectionSx}>
        <Stack gap={0.5} mb={2.5}>
          <Typography variant="h6" fontWeight={700}>
            Rentabilidade das cotas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visão histórica das cotas selecionadas, com retorno absoluto e
            comparação com CDI.
          </Typography>
        </Stack>

        {selectedFunds && selectedFunds.length > 0 && (
          <Box mb={2}>
            <ClassSeriesSelector
              funds={selectedFunds}
              referenceDate={baseMonth ?? ''}
              onSelectionsChange={handleSelectionsChange}
              noDataFunds={noDataFunds}
            />
          </Box>
        )}

        {rentabilityControls && <Box mb={2}>{rentabilityControls}</Box>}

        <Stack gap={2}>
          {isProfitabilityError && (
            <Alert severity="error" variant="outlined">
              Erro ao carregar dados de rentabilidade das cotas selecionadas.
            </Alert>
          )}
          {isProfitabilityLoading && (
            <Alert severity="info" variant="outlined">
              Carregando rentabilidade das cotas selecionadas...
            </Alert>
          )}
          {!isProfitabilityLoading &&
            !isProfitabilityError &&
            profitabilityData && (
              <>
                <HistoricalProfitabilityTable
                  response={profitabilityData}
                  mode="absolute"
                  orderedFunds={uniqueFunds}
                  referenceDate={baseMonth}
                />
                <HistoricalProfitabilityTable
                  response={profitabilityData}
                  mode="cdi"
                  orderedFunds={uniqueFunds}
                  referenceDate={baseMonth}
                />
              </>
            )}
          <LineChart
            title="Rentabilidade Mensal (%)"
            titleHelp={<RentabilidadeHelp />}
            subtitle={competenciaSubtitle}
            series={monthlyReturnsSeries}
            benchmarkSeriesCount={monthlyReturnsSeries.length > 0 ? 1 : 0}
            tooltipFormatter={SIGNED_PERCENT_FORMATTER}
            selectedPeriod={monthlyReturnsPeriod}
            periodOptions={PROFITABILITY_PERIOD_OPTIONS}
            onPeriodChange={(p) =>
              setMonthlyReturnsPeriod(p as ProfitabilityPeriodKey)
            }
            selectorId="monthly-returns"
            noDataText="Sem dados de rentabilidade mensal para a competência selecionada"
          />
          <LineChart
            title="Rentabilidade Mensal (% do CDI)"
            titleHelp={monthlyCdiHelp}
            subtitle={competenciaSubtitle}
            series={monthlyCdiSeries}
            yAxisFormatter={(v) =>
              v == null ? 'Sem dado' : `${v.toFixed(0)}%`
            }
            tooltipFormatter={(v) =>
              v == null ? 'Sem dado' : `${v.toFixed(2)}%`
            }
            selectedPeriod={monthlyCdiPeriod}
            periodOptions={PROFITABILITY_PERIOD_OPTIONS}
            onPeriodChange={(p) =>
              setMonthlyCdiPeriod(p as ProfitabilityPeriodKey)
            }
            selectorId="monthly-returns-cdi"
            noDataText="Sem dados de rentabilidade mensal para a competência selecionada"
          />
          <LineChart
            title="Rentabilidade acumulada no período (%)"
            titleHelp="Retorno composto acumulado de cada ativo ao longo da competência selecionada. Calculado como produto encadeado dos retornos mensais."
            subtitle={competenciaSubtitle}
            series={accumulatedSeries}
            benchmarkSeriesCount={accumulatedSeries.length > 0 ? 1 : 0}
            tooltipFormatter={SIGNED_PERCENT_FORMATTER}
            selectedPeriod={accumulatedPeriod}
            periodOptions={PROFITABILITY_PERIOD_OPTIONS}
            onPeriodChange={(p) =>
              setAccumulatedPeriod(p as ProfitabilityPeriodKey)
            }
            selectorId="accumulated-returns"
            noDataText="Sem dados de rentabilidade acumulada para a competência selecionada"
          />
        </Stack>
      </Paper>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2, lineHeight: 1.6 }}>
        * Dados de rentabilidade disponíveis a partir de dezembro de 2025,{' '}
        quando o Ofício Circular CVM/SSE 09/25 estabeleceu um código único para
        cada cota, permitindo reconstruir com consistência o histórico de{' '}
        valores unitários, amortizações, emissões e resgates.
      </Typography>
    </Stack>
  );
}
